import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface SeoConfig {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  keywords?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private meta = inject(Meta);
  private titleService = inject(Title);

  private defaultConfig: SeoConfig = {
    title: 'HypeTrack - Top Greek Artists & Songs Rankings',
    description: 'Discover the top Greek music artists and songs. Real-time rankings based on Spotify listeners, YouTube subscribers, and Instagram followers.',
    image: 'https://www.hypetrack.gr/hypetrack-text.png',
    url: 'https://www.hypetrack.gr',
    type: 'website',
    keywords: ['Greek music', 'Greek artists', 'music charts', 'Greek songs', 'Spotify Greece', 'YouTube Greece']
  };

  updateTags(config: SeoConfig): void {
    const seoConfig = { ...this.defaultConfig, ...config };

    // Update title
    if (seoConfig.title) {
      this.titleService.setTitle(seoConfig.title);
      this.meta.updateTag({ property: 'og:title', content: seoConfig.title });
      this.meta.updateTag({ name: 'twitter:title', content: seoConfig.title });
    }

    // Update description
    if (seoConfig.description) {
      this.meta.updateTag({ name: 'description', content: seoConfig.description });
      this.meta.updateTag({ property: 'og:description', content: seoConfig.description });
      this.meta.updateTag({ name: 'twitter:description', content: seoConfig.description });
    }

    // Update image
    if (seoConfig.image) {
      this.meta.updateTag({ property: 'og:image', content: seoConfig.image });
      this.meta.updateTag({ name: 'twitter:image', content: seoConfig.image });
      this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    }

    // Update URL
    if (seoConfig.url) {
      this.meta.updateTag({ property: 'og:url', content: seoConfig.url });
      this.meta.updateTag({ name: 'twitter:url', content: seoConfig.url });
    }

    // Update type
    if (seoConfig.type) {
      this.meta.updateTag({ property: 'og:type', content: seoConfig.type });
    }

    // Update keywords
    if (seoConfig.keywords && seoConfig.keywords.length > 0) {
      this.meta.updateTag({ name: 'keywords', content: seoConfig.keywords.join(', ') });
    }

    // Additional Open Graph tags
    this.meta.updateTag({ property: 'og:site_name', content: 'HypeTrack' });
    this.meta.updateTag({ property: 'og:locale', content: 'en_US' });
    this.meta.updateTag({ property: 'og:locale:alternate', content: 'el_GR' });
  }

  setDefaultTags(): void {
    this.updateTags(this.defaultConfig);
  }

  updateCanonicalUrl(url: string): void {
    let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');

    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }
}
